// ProductList.js
import React, { useState, useEffect, useCallback } from 'react';
import './ProductList.css';
import { useCart } from './CartContext';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '../api';

// ✅ Create a separate component for product image with error handling
const ProductImage = ({ src, alt }) => {
  const [hasError, setHasError] = useState(false);

  if (hasError || !src) {
    return (
      <div className="no-image-placeholder">
        <span>🥬</span>
        <span>No Image</span>
      </div>
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      className="product-image"
      onError={() => setHasError(true)}  // ✅ Sets state, prevents loop
    />
  );
};

const ProductList = ({ selectedCategory = "All" }) => {
  const [products, setProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const { addToCart, buyNow, resetCheckout } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setIsLoading(true);
        const response = await apiClient.getProducts();
        
        const processedProducts = response.data.map(product => ({
          ...product,
          price: product.price ? parseFloat(product.price) : 0,
          imageUrl: product.image ? product.image : null
        }));
        
        setProducts(processedProducts);
      } catch (error) {
        console.error('Error fetching products:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProducts();
  }, []);

  const filteredProducts = products.filter(product => {
    const matchesCategory = selectedCategory === "All" || 
      product.category?.toLowerCase() === selectedCategory.toLowerCase();
    
    const matchesSearch = !searchTerm || 
      product.name?.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesCategory && matchesSearch;
  });

  const handleAddToCart = (product) => {
    const productData = {
      id: product.id,
      name: product.name,
      price: product.price,
      quantity: 1,
      imageUrl: product.imageUrl,
      category: product.category
    };
    addToCart(productData);
  };

  const handleBuyNow = useCallback((product) => {
    const productData = {
      id: product.id,
      name: product.name,
      price: product.price,
      quantity: 1,
      imageUrl: product.imageUrl,
      category: product.category
    };
    resetCheckout();
    buyNow(productData);
    navigate('/checkout');
  }, [resetCheckout, buyNow, navigate]);

  return (
    <div className="product-list">
      <div className="search-container">
        <input
          type="text"
          placeholder={selectedCategory !== "All" 
            ? `Search ${selectedCategory}...` 
            : "Search all products..."}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <div className="products-grid-container">
        {isLoading ? (
          <div className="loading-message">Loading products...</div>
        ) : filteredProducts.length > 0 ? (
          filteredProducts.map((product) => (
            <div key={product.id} className="product-card">
              <div className="product-image-container">
                {/* ✅ Use ProductImage component with proper error handling */}
                <ProductImage 
                  src={product.imageUrl} 
                  alt={product.name} 
                />
              </div>
              <div className="product-info">
                <h3>{product.name}</h3>
                <p>₹{Number(product.price).toFixed(2)}</p>
                <p className="product-category">{product.category}</p>
                <div className="button-group">
                  <button 
                    onClick={() => handleAddToCart(product)}
                    className="product-button"
                  >
                    Add to Cart
                  </button>
                  <button
                    onClick={() => handleBuyNow(product)}
                    className="product-button"
                  >
                    Buy Now
                  </button>
                </div>
              </div>
            </div>
          ))
        ) : (
          <p className="no-products-message">
            {products.length === 0 ? 'No products available' : 'No matching products found'}
          </p>
        )}
      </div>
    </div>
  );
};

export default React.memo(ProductList);